import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchUnreadNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";

const POLL_MS = 25000;

/**
 * NotificationBell — navbar icon with red unread dot + popover list.
 *
 * Polls /api/notifications/unread every 25s while signed in. Clicking a
 * mention navigates to the league chat and marks just that one read;
 * "Mark all read" clears the dot in one tap.
 *
 * Also exposes a window event `lh:notification-bump` so other components
 * (e.g. LeagueChat) can trigger an immediate refresh after a relevant action.
 */
export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) { setItems([]); setCount(0); return; }
    try {
      const d = await fetchUnreadNotifications();
      setItems(d.items || []);
      setCount(d.count || 0);
    } catch (_e) { /* silent — no toast for background poll */ }
  }, [user]);

  useEffect(() => {
    refresh();
    if (!user) return undefined;
    const t = setInterval(refresh, POLL_MS);
    const bump = () => refresh();
    window.addEventListener("lh:notification-bump", bump);
    return () => {
      clearInterval(t);
      window.removeEventListener("lh:notification-bump", bump);
    };
  }, [user, refresh]);

  // Click-outside close
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;

  const onPick = async (n) => {
    setOpen(false);
    try { await markNotificationRead(n.notif_id); } catch (_e) { /* noop */ }
    refresh();
    // Land on the league page so the user can see the message inline.
    if (n?.payload?.league_id) {
      navigate(`/leagues?lid=${encodeURIComponent(n.payload.league_id)}&tab=chat`);
    }
  };

  const clearAll = async () => {
    try { await markAllNotificationsRead(); } catch (_e) { /* noop */ }
    refresh();
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell"
        aria-label={`${count} unread notifications`}
        className="relative w-9 h-9 rounded-full border border-white/15 hover:border-[#14b8a6]/50 flex items-center justify-center text-white/70 hover:text-white transition-colors"
      >
        <i className={`fas fa-bell ${count > 0 ? "animate-pulse" : ""}`} aria-hidden="true" />
        {count > 0 && (
          <span
            data-testid="notification-bell-count"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#14b8a6] text-black text-[9px] font-bold flex items-center justify-center"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-bell-popover"
          className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-y-auto rounded-2xl border border-[#14b8a6]/30 bg-black/95 shadow-[0_8px_30px_rgba(0,0,0,0.6)] z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-bell mr-1" />NOTIFICATIONS
            </p>
            {count > 0 && (
              <button
                type="button"
                onClick={clearAll}
                data-testid="notification-mark-all-read"
                className="text-[10px] tracking-widest text-white/55 hover:text-white"
              >
                MARK ALL READ
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p
              data-testid="notification-empty"
              className="text-center text-sm text-white/50 py-10"
            >
              <i className="fas fa-feather text-2xl block mb-2 text-white/30" /> 
              You're all caught up.
            </p>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((n) => (
                <li key={n.notif_id}>
                  <button
                    type="button"
                    onClick={() => onPick(n)}
                    data-testid={`notification-row-${n.notif_id}`}
                    className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-[#14b8a6]/5 transition-colors"
                  >
                    <i className="fas fa-at text-[#14b8a6] mt-0.5" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] text-white/80">
                        <span className="font-bold text-[#14b8a6]">@{n.payload?.sender_name || "Someone"}</span> mentioned you
                        {n.payload?.league_name ? <> in <span className="text-[#14b8a6]">{n.payload.league_name}</span></> : null}
                      </p>
                      <p className="text-[12px] text-white/55 mt-0.5 truncate">"{n.payload?.snippet}"</p>
                      <p className="text-[10px] text-white/35 mt-1">
                        {new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
